import { useContext } from "react";
import { SearchContext } from "../contexts/WithSearch/SearchContext";
import { LoadingSpinner } from "../bits/loading";
import { SearchResultList } from "../bits/SearchResultList";
import { css, useTheme } from "@emotion/react";
import InfoIcon from "@assets/info.svg";
import { FlexRowContainer } from "../bits/layout";
import { styled, useMediaQuery } from "@mui/material";
import { pxToRem } from "../../design/utils";
import { useTitle } from "../hooks/useTitle";
import { PublicationFilter } from "../bits/PublicationFilter";
import { trpc } from "../../utils/trpc";
import { FilterHeader } from "../headers/FilterHeader";
import { ResultsHeader } from "../headers/ResultsHeader";
import { Link } from "react-router";
import { AISearchSummary } from "../patterns/AISearchSummary";
import { useUser } from "../hooks/useUser";

const SearchChip = styled("div")(
  ({ theme }) => css`
    ${theme.customTypography.label._2}
    background: white;
    border-radius: 6px;
    padding: 0px 4px;
  `,
);

export const SearchResults = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useUser();
  const {
    searchQuery,
    results,
    isLoading,
    fetchStatus,
    selectedPublications,
    relatedSearches,
    setSelectedPublications,
  } = useContext(SearchContext);
  const { data: publications } = trpc.getPublications.useQuery();
  useTitle(`Search - ${searchQuery} | Compass`);

  const totalPublications = Object.keys(
    results?.facets?.publicationId || {},
  ).length;

  return (
    <>
      <div
        css={css`
          position: relative;
        `}
      >
        <ResultsHeader>
          {results && (
            <>
              <FlexRowContainer>
                <SearchChip>
                  {`${(results?.nbHits ?? 0) > 1000 ? "1000+" : (results?.nbHits ?? 0)} ${results?.nbHits === 1 ? "result" : "results"}`}
                </SearchChip>
                /
                <SearchChip>
                  {`in ${totalPublications} ${totalPublications === 1 ? "publication" : "publications"}`}
                </SearchChip>
              </FlexRowContainer>
              {isMobile && (
                <h1
                  style={{
                    ...theme.customTypography.jumbo._5,
                  }}
                  css={({ breakpoints, colours }) => css`
                    color: ${colours.primary.dark._20};
                    padding-left: 1rem;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    @media (max-width: ${breakpoints.values.sm}px) {
                      display: none;
                    }
                  `}
                >
                  Showing results for "{`${searchQuery}`}"
                </h1>
              )}
            </>
          )}
        </ResultsHeader>
        <FilterHeader hasRelated={relatedSearches.length > 0}>
          <PublicationFilter
            publications={publications}
            selectedPublications={selectedPublications}
            setSelectedPublications={setSelectedPublications}
            includeFRJ={true}
          />
          {relatedSearches.length > 0 && (
            <FlexRowContainer
              css={({ customTypography }) => css`
                ${customTypography.label._3}
                justify-content: left;
                flex-wrap: wrap;
                padding-bottom: ${theme.spacing(1)};
              `}
            >
              Related Searches:
              {relatedSearches.map((relatedSearch) => (
                <Link
                  to={`/search?search="${relatedSearch}"`}
                  css={({ customTypography, colours }) => css`
                    ${customTypography.label._3}
                    color: ${colours.primary.mid.dark20};
                    text-decoration: underline;
                    border: none;
                    background: none;
                    padding: 0;
                  `}
                >
                  {relatedSearch}
                </Link>
              ))}
            </FlexRowContainer>
          )}
        </FilterHeader>

        <div
          css={({ layers, spacing }) => css`
            height: 100%;
            ${layers.relief.uiGrey_5.noShadow}
            overflow-y: auto;
            scrollbar-width: thin;
            padding: ${spacing(1)};
            border-radius: 16px;
            margin: 1rem;
            height: calc(100vh - ${pxToRem(160)});
            height: calc(100dvh - ${pxToRem(160)});
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          {user?.aiAccess && searchQuery && searchQuery.length > 2 && (
            <AISearchSummary searchQuery={searchQuery} />
          )}
          {isLoading && searchQuery && searchQuery.length > 2 && (
            <LoadingSpinner />
          )}
          {results &&
            !isLoading &&
            (results.hits.length ? (
              <SearchResultList
                searchResults={results.hits}
                cropTo={2000}
                searchTerm={searchQuery}
              />
            ) : (
              <div>No results found</div>
            ))}
          {typeof searchQuery === "string" &&
            fetchStatus === "idle" &&
            searchQuery.length <= 2 && (
              <FlexRowContainer
                css={({ customTypography, colours }) => css`
                  justify-content: center;
                  gap: 4px;
                  padding-top: 16px;
                  ${customTypography.label._1}
                  color: ${colours.accent.dark._30};
                `}
              >
                <img src={InfoIcon} height={"12px"} />
                <div>Search for more than 2 characters to get results</div>
              </FlexRowContainer>
            )}
        </div>
      </div>
    </>
  );
};
